import { Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { useGeoMarkupStore } from '@/store';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import { performStatusChange } from '@/utils/statusChangeHelper';

import { BasicInfoFields } from './components/BasicInfoFields';
import { TaskDescription } from './components/TaskDescription';
import { TaskProgressSummary } from './components/TaskProgressSummary';
import { SectionHeader } from './components/SectionHeader';
import { TaskGeneralInfoProps } from './TaskGeneralInfo.types';

export const TaskGeneralInfo = ({
  task,
  onTabChange,
  readOnly = false,
}: TaskGeneralInfoProps) => {
  const { t } = useTranslation();

  const setTaskStatus = useGeoMarkupStore((state) => state.tasks.setTaskStatus);
  const previewMode = useGeoMarkupStore(
    (state) => state.ui.slidePanel.previewMode
  );
  const { isMobile } = useDeviceLayout();

  const handleStatusChange = (status: typeof task.status) => {
    performStatusChange(task.id, status, task.status, setTaskStatus);
  };

  // Hide description in preview mode on mobile to show only basic info
  const showDescription = !(previewMode && isMobile);

  // Check if TaskProgressSummary will render anything
  const hasProgressSummary =
    task.requirements.length > 0 || task.relatedTasks.length > 0;

  // Check if description exists and is not empty
  const hasDescription = task.description && task.description.trim() !== '';

  return (
    <Stack gap="lg">
      {/* Basic Info Section - no header, primary content */}
      <BasicInfoFields
        task={task}
        onStatusChange={handleStatusChange}
        readOnly={readOnly}
      />

      {/* Progress Section */}
      {hasProgressSummary && (
        <>
          <SectionHeader title={t('taskGeneralInfo.sections.progress')} />
          <TaskProgressSummary task={task} onTabChange={onTabChange} />
        </>
      )}

      {/* Description Section */}
      {showDescription && hasDescription && (
        <>
          <SectionHeader title={t('taskGeneralInfo.sections.description')} />
          <TaskDescription description={task.description} isMobile={isMobile} />
        </>
      )}
    </Stack>
  );
};
